import React from 'react'
import { cn } from '@/lib/utils'
import { Info } from 'lucide-react'
import type { Adjustment } from '@/types/calendar'
import { paceBand, paceComparison, formatPoints, type PaceKey } from '@/lib/plain-language'

/**
 * Shared "why this price" reasoning infrastructure for the calendar hover card (PL2) and the
 * day-detail modal (PL3).
 *
 * PLAIN-LANGUAGE RULE (docs/revenue-vocabulary.md § Plain-language presentation): every block
 * is split into
 *   (a) a plain-language CONCLUSION that stays visible on the glance surface, and
 *   (b) raw engine values (`0.53x`, `σ`, samples, amplifier, computed→applied) that live ONLY
 *       inside `EngineDetails` — surfaced by `adjustmentDiagnostics()` for modals/pages to
 *       render inside the collapsed disclosure. Hover cards lose Tier-3 entirely.
 *
 * THIS FILE IS THE SINGLE COPY OF THAT REASONING. `adjustmentReasoning` is module-private and
 * `AdjustmentLabel` is imported by exactly two surfaces — `CalendarDayPopup` (the hover card,
 * mounted by all six calendar grids) and `DayDetailOverview` (the day-detail modal body). Both
 * describe the SAME adjustment on the same date, so the app's consistency invariant (cell =
 * hover card = modal) applies to the words as much as to the money: change what a stage says
 * here and both surfaces move together. Copy this logic into one surface to tweak it there and
 * the operator gets two different explanations for one price.
 * Guarded by `test/lib/frontend_adjustment_context_convention_test.rb`.
 */

// Booking-pace conclusion on the one worded five-band scale (no bare multiplier).
const BOOKING_PACE_SENTENCE: Record<PaceKey, string> = {
  well_ahead: 'Booking well ahead for this date',
  ahead: 'Booking ahead for this date',
  on_track: 'Booking on track for this date',
  behind: 'Booking behind for this date',
  well_behind: 'Booking well behind for this date',
  unknown: 'Demand read for this date',
}

/** Build the plain-language conclusion string (visible). Never contains raw engine values. */
function adjustmentReasoning(adj: Adjustment): string | null {
  if (adj.stage === 'property_demand') {
    const parts: string[] = []

    if (adj.type === 'event_deferred') {
      parts.push('Event prediction trusted for now')
      parts.push('No booking history yet — this will settle as bookings come in')
      if (adj.baseline_adjustment_detail?.event_name) {
        parts.push(`Event: ${adj.baseline_adjustment_detail.event_name}`)
      }
      return parts.join('\n')
    }

    // Conclusion — worded booking pace.
    if (adj.pace_ratio != null) {
      parts.push(BOOKING_PACE_SENTENCE[paceBand(adj.pace_ratio).key])
    } else if (adj.property_occ != null) {
      parts.push('Demand read for this date')
    }

    // Plain comparison: how full vs how full it usually is by now.
    const baseline = adj.baseline_adjusted ? adj.baseline_adjusted_occ : adj.baseline_occ
    const comparison = paceComparison(adj.property_occ, baseline)
    if (comparison) parts.push(comparison)

    if (adj.baseline_adjusted && adj.baseline_adjustment_detail?.event_name) {
      parts.push(`Adjusted for ${adj.baseline_adjustment_detail.event_name}`)
    }

    if (adj.confidence_factor != null && adj.confidence_factor < 1.0) {
      parts.push('Eased back toward normal — limited history for this date')
    }

    if (adj.sensitivity) {
      parts.push(`${capitalize(adj.sensitivity)} sensitivity setting`)
    }

    return parts.length > 0 ? parts.join('\n') : null
  }

  if (adj.stage === 'room_demand') {
    const parts: string[] = []
    const noun = adj.scope === 'room_group' ? 'room family' : 'room'

    if (adj.type === 'pace_divergence') {
      if (adj.divergence != null) {
        parts.push(`This ${noun} is doing ${adj.divergence >= 0 ? 'better' : 'worse'} than the hotel overall`)
        parts.push(`${formatPoints(adj.divergence)} vs the hotel average`)
      } else {
        parts.push(`This ${noun} is tracking the hotel`)
      }
    } else {
      if (adj.room_occ != null && adj.property_occ != null) {
        parts.push(`This ${noun} is ${adj.room_occ >= adj.property_occ ? 'fuller' : 'quieter'} than the hotel average`)
        parts.push(`${Math.round(adj.room_occ)}% full · hotel average ${Math.round(adj.property_occ)}%`)
      } else if (adj.divergence != null) {
        parts.push(`This ${noun} is ${adj.divergence >= 0 ? 'ahead of' : 'behind'} the hotel average`)
        parts.push(`${formatPoints(adj.divergence)} vs the hotel average`)
      }
    }

    if (adj.scope === 'room_group' && adj.member_count != null && adj.family_occ_pct != null) {
      parts.push(`Priced as a family of ${adj.member_count} · ${Math.round(adj.family_occ_pct)}% full`)
    }

    if (adj.type === 'pace_divergence' && adj.confidence_factor != null && adj.confidence_factor < 1.0) {
      parts.push('Eased back toward neutral — limited history for this room')
    }

    if (adj.sensitivity) {
      parts.push(`${capitalize(adj.sensitivity)} sensitivity setting`)
    }

    return parts.length > 0 ? parts.join('\n') : null
  }

  if (adj.stage === 'rate_stability') {
    const parts: string[] = []
    const cap = adj.description?.match(/\d+%/)?.[0] ?? null
    if (adj.type === 'decrease_dampened') {
      parts.push('Eased down to keep the change gradual')
      parts.push(
        'The new rate dropped sharply, so the engine limits how far a rate can fall in one cycle to keep prices stable for guests and OTAs.',
      )
      if (cap) parts.push(`Limited to a ${cap} drop this cycle`)
    } else if (adj.type === 'increase_capped') {
      parts.push('Held the rise back to keep the change gradual')
      parts.push(
        'The new rate jumped sharply, so the engine limits how far a rate can rise in one cycle to keep prices stable for guests and OTAs.',
      )
      if (cap) parts.push(`Limited to a ${cap} rise this cycle`)
    }
    // Say what the limit is measured from. It is the price guests can book right now, not the
    // last figure the engine worked out, and where those two have drifted apart the cap looks
    // arbitrary unless we name it. The exact amount is a Tier-3 value — it belongs in
    // adjustmentDiagnostics(), not in this tooltip.
    if (parts.length > 0) {
      parts.push('Measured from the price guests can book right now')
      parts.push('Setting a price yourself takes effect straight away — easing only applies to the engine’s own changes.')
    }
    return parts.length > 0 ? parts.join('\n') : null
  }

  if (adj.stage === 'target_pressure' && adj.type === 'forecast_gap') {
    // Dormant signals get their own visible explanation elsewhere — no gap wording here.
    if (adj.dormant_reason) return null
    const usesDayType = adj.day_type != null && adj.day_type_gap != null
    const gap = usesDayType ? adj.day_type_gap : adj.target_gap
    if (gap == null) return null
    const subject = adj.day_type === 'weekend' ? 'Weekends' : adj.day_type === 'weekday' ? 'Weekdays' : 'This month'
    const parts: string[] = []
    parts.push(gap > 0 ? `${subject} running ahead of target` : `${subject} tracking behind target`)
    // The date's own day-type gap (D13/C6) — moved off the modal surface into this tooltip.
    parts.push(`${subject} vs your target: ${formatPoints(gap)}`)
    // C8 dilution guard (wins over the ADR floor) and C7 ADR floor — short plain
    // conclusions moved off the modal surface into this tooltip. Only live (non-dormant)
    // forecast_gap reaches here, so these notes only ever apply to a real cut.
    if (adj.dilution_guarded) {
      parts.push(
        adj.lead_time_days != null
          ? `Discount withheld — at ${adj.lead_time_days} day${adj.lead_time_days === 1 ? '' : 's'} out, demand books regardless of price.`
          : 'Discount withheld — close-in demand books regardless of price.',
      )
    } else if (adj.adr_limited) {
      parts.push('Held at your ADR floor — target wanted a deeper cut.')
    }
    return parts.join('\n')
  }

  // Last-Minute Clearance (plan §7). The stage's own `description` already states the two facts —
  // rooms unsold and selling time left — on both surfaces that render this row, so the tooltip
  // says the things a price cut makes an operator want to check instead, and repeats no number.
  if (adj.stage === 'clearance') {
    return [
      'This night is heading for empty rooms',
      'The price steps down along the line on your Clearance page — never up while the night is clearing, and never past your floor or the room\'s own minimum. Change that minimum, or set the price yourself, and it moves there instead.',
      'It goes back to normal when the rooms are gone, or when the night stops heading for empty rooms — one sale with more rooms still going empty keeps the lower price working.',
    ].join('\n')
  }

  if (adj.stage === 'linked_parent') {
    const parts: string[] = []
    parts.push(
      adj.parent_room_type_name ? `Parent: ${adj.parent_room_type_name}` : 'Linked to parent room type',
    )
    parts.push(
      "This room is linked — its rate follows the parent's computed rate, then applies the linked offset. It does not run its own demand, event, or gap-filling stages.",
    )
    return parts.join('\n')
  }

  // A Same room's offset from its twin (combined inventory). The row already shows the offset and the
  // price it ends on; the tooltip says where the offset is set and what it never changes.
  if (adj.stage === 'same_room_offset') {
    const twin = adj.twin_room_type_name ?? 'the lead room'
    return [
      `This listing sells the same rooms as ${twin}, at ${twin}'s price plus this offset.`,
      `The offset is set in the room mapping on the Combined inventory page. Restrictions and rooms left to sell still follow ${twin}.`,
    ].join('\n')
  }

  if (adj.stage === 'event') {
    const parts: string[] = []
    if (adj.event_name) parts.push(adj.event_name)
    return parts.length > 0 ? parts.join('\n') : null
  }

  return null
}

/**
 * WHY A CLEARED PRICE STOPPED FALLING (2026-09-02).
 *
 * ClearanceStage bounds the price itself now, so ClampStage no longer writes "Clamped to floor
 * £55" beneath it and the reason survives only in `terminal_source`. The hover card gets it
 * inside the stage's own composed sentence; the day modal renders no such sentence, so it prints
 * this one short muted line under the clearance row instead. Both read the SAME backend key —
 * this function classifies nothing, derives nothing, and never sees a number.
 *
 * Absent key ⟹ null: the rung decided its own price and there is nothing to explain.
 */
export function clearanceTerminalNote(adj: Adjustment): string | null {
  if (adj.stage !== 'clearance') return null
  switch (adj.terminal_source) {
    case 'floor':
      return 'Held at your own minimum for this room — the step wanted to go deeper.'
    case 'operator_limit':
      return 'Held at your own limit on how deep clearance goes.'
    case 'engine_limit':
      return "Held at the engine's own limit on how deep clearance goes."
    default:
      return null
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** A single verbatim diagnostic row destined for `EngineDetails`. */
export interface DiagnosticRow {
  label: string
  value: string
}

/**
 * The raw, VERBATIM engine values for an adjustment — the Tier-3 internals that must stay
 * reachable one click away (inside `EngineDetails`) for catching data bugs, but never appear
 * on a glance surface. Values are kept in their original precision on purpose: prettifying a
 * diagnostic defeats its bug-catching purpose. Returns `[]` for stages with no raw internals.
 */
// plain-language-ignore-start — adjustmentDiagnostics builds the VERBATIM Tier-3 rows
// (`Pace ratio 0.53x`, `σ`, ` pts`) that render ONLY inside EngineDetails. This is diagnostics
// content, not a glance surface; see docs/revenue-vocabulary.md § Plain-language presentation.
export function adjustmentDiagnostics(adj: Adjustment): DiagnosticRow[] {
  const rows: DiagnosticRow[] = []
  const push = (label: string, value: string | null | undefined) => {
    if (value != null) rows.push({ label, value })
  }

  if (adj.stage === 'property_demand') {
    if (adj.pace_ratio != null) push('Pace ratio', `${Number(adj.pace_ratio).toFixed(2)}x`)
    if (adj.property_occ != null) push('Property occupancy', `${Number(adj.property_occ).toFixed(0)}%`)
    if (adj.baseline_adjusted && adj.baseline_adjusted_occ != null) {
      push('Event-adjusted baseline', `${Number(adj.baseline_adjusted_occ).toFixed(0)}%`)
      if (adj.baseline_normal_occ != null) push('Normal baseline', `${Number(adj.baseline_normal_occ).toFixed(0)}%`)
    } else if (adj.baseline_occ != null) {
      push('Baseline', `${Number(adj.baseline_occ).toFixed(0)}%`)
    }
    if (adj.lead_time_factor != null && adj.lead_time_factor !== 1.0) {
      push('Lead time', `${adj.lead_time_days ?? 0}d`)
      push('Amplifier', `${Number(adj.lead_time_factor).toFixed(1)}x`)
    }
    if (adj.confidence_factor != null && adj.confidence_factor < 1.0) {
      push('Baseline confidence', `${Number(adj.confidence_factor).toFixed(2)}x`)
      if (adj.baseline_sample_size != null) push('Samples', `${adj.baseline_sample_size}`)
      if (adj.baseline_stddev != null) push('σ', `${Number(adj.baseline_stddev).toFixed(1)}`)
    }
    return rows
  }

  if (adj.stage === 'room_demand') {
    const isFamily = adj.scope === 'room_group'
    if (adj.type === 'pace_divergence') {
      if (adj.room_pace != null) push(isFamily ? 'Family pace' : 'Room pace', `${Number(adj.room_pace).toFixed(2)}x`)
      if (adj.property_pace != null) push('Property pace', `${Number(adj.property_pace).toFixed(2)}x`)
      if (adj.divergence != null) push('Divergence', `${adj.divergence >= 0 ? '+' : ''}${Number(adj.divergence).toFixed(0)} pts`)
      if (adj.confidence_factor != null && adj.confidence_factor < 1.0) {
        push('Baseline confidence', `${Number(adj.confidence_factor).toFixed(2)}x`)
        if (adj.room_confidence_factor != null) push('Room confidence', `${Number(adj.room_confidence_factor).toFixed(2)}x`)
        if (adj.property_confidence_factor != null) push('Property confidence', `${Number(adj.property_confidence_factor).toFixed(2)}x`)
      }
    } else {
      if (adj.room_occ != null) push(isFamily ? 'Family occupancy' : 'Room occupancy', `${Number(adj.room_occ).toFixed(0)}%`)
      if (adj.property_occ != null) push('Property average', `${Number(adj.property_occ).toFixed(0)}%`)
      if (adj.divergence != null) push('Divergence', `${adj.divergence >= 0 ? '+' : ''}${Number(adj.divergence).toFixed(0)} pts`)
    }
    if (isFamily && adj.member_count != null && adj.family_occ_pct != null) {
      push('Family', `${adj.member_count} rooms · ${Number(adj.family_occ_pct).toFixed(0)}%`)
    }
    if (adj.lead_time_factor != null && adj.lead_time_factor !== 1.0) {
      push('Lead time', `${adj.lead_time_days ?? 0}d`)
      push('Amplifier', `${Number(adj.lead_time_factor).toFixed(1)}x`)
    }
    return rows
  }

  if (adj.stage === 'clearance') {
    if (adj.unsold_units != null && adj.family_capacity != null) {
      push('Unsold', `${adj.unsold_units} of ${adj.family_capacity}`)
    }
    if (adj.projected_finish_pct != null) push('Projected finish', `${Number(adj.projected_finish_pct).toFixed(1)}%`)
    if (adj.band_lead != null) push('Band lead', `${adj.band_lead}d`)
    if (adj.hours_left != null) push('Selling hours left', `${adj.hours_left}`)
    if (adj.pre_clearance_rate != null) push('Before clearance', `${Number(adj.pre_clearance_rate).toFixed(0)}`)
    return rows
  }

  if (adj.stage === 'rate_stability') {
    if (adj.computed_rate != null && adj.applied_rate != null) {
      push('Computed', `${Number(adj.computed_rate).toFixed(0)}`)
      push('Applied', `${Number(adj.applied_rate).toFixed(0)}`)
    }
    // The published price the per-cycle limit was measured from.
    if (adj.anchor_rate != null) push('Published', `${Number(adj.anchor_rate).toFixed(0)}`)
    return rows
  }

  return rows
}
// plain-language-ignore-end

/**
 * Wraps an adjustment label with a CSS-only hover tooltip showing the plain-language
 * reasoning. Tier-3 raw values are intentionally absent here (hover cards lose Tier-3) —
 * modals surface them separately via `adjustmentDiagnostics()` inside `EngineDetails`.
 *
 * Use as: <AdjustmentLabel adj={adj}>{label text}</AdjustmentLabel>
 *
 * SHARED BY BOTH CALENDAR SURFACES — change it for both, never fork it for one.
 *
 * This is NOT a public API and the props are NOT frozen (audited 2026-08-12, Wave 2 batch 2d).
 * There are two consumers, `CalendarDayPopup` and `DayDetailOverview`, both in this repo and
 * both type-checked, so `yarn typecheck` finds every caller of a prop change in one run —
 * changing the signature is cheap and safe. The expensive mistake is the other one: copying
 * this component (or `adjustmentReasoning`) into one surface so a stage can be worded
 * differently there. The hover card and the modal explain the same adjustment on the same
 * date, and an operator who sees two answers stops trusting both. See the file header.
 */
export default function AdjustmentLabel({
  adj,
  children,
}: {
  adj: Adjustment
  children: React.ReactNode
}) {
  const reasoning = adjustmentReasoning(adj)
  const isEventAdjusted = adj.stage === 'property_demand' && adj.baseline_adjusted

  if (!reasoning && !isEventAdjusted) return <span className="text-muted-foreground">{children}</span>

  const lines = reasoning ? reasoning.split('\n') : []

  return (
    <span className="group/adj relative inline-flex items-center gap-1 text-muted-foreground cursor-help">
      {children}
      {isEventAdjusted && (
        <span className="rounded bg-slate-100 px-1 py-0.5 text-[10px] font-medium text-slate-600">
          event-adjusted
        </span>
      )}
      {reasoning && (
        <Info className="h-3 w-3 text-muted-foreground/40 group-hover/adj:text-primary/60 transition-colors shrink-0" />
      )}
      <span className="pointer-events-none invisible group-hover/adj:visible absolute left-0 top-full z-50 mt-1 w-max max-w-[260px] rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg">
        {lines.map((line, i) => (
          <span key={i} className={cn('block text-[11px] leading-relaxed', i === 0 ? 'font-medium text-foreground' : 'text-muted-foreground')}>
            {line}
          </span>
        ))}
      </span>
    </span>
  )
}
